import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import styled from 'styled-components';

import { FieldArray } from 'formik';

import { Button, Feedback, Flexer, Spacer, Title } from '@ontruck/orc';

import { Loading } from 'components/Loading';

import { StyledLoading, StyledThreeColumnsChild } from '../styled';

import { ManualPriceWarning, ShipperSelector, ShipperUserSelector } from '../fields';
import ClientReference from './fields/ClientReference';

import { DriverVehicleSection, ExtrasSection, SummarySection } from '../sections';

import {
  addAddress,
  formChange,
  shipperChange,
  userChange,
} from 'redux/modules/shipper-order-form/effects';

import {
  getQuote,
  getShipperAddresses,
  getShipperUsers,
  getUserInfo,
  isMainLoading,
  isSubmiting,
} from 'redux/modules/shipper-order-form/selectors';

import {
  baseLoad,
  baseShipment,
  COLLECTION,
  DISTRIBUTION,
  dropoffStopInfo,
  EXTRAS_FIELD,
  HANDLING_LOADING,
  HANDLING_UNLOADING,
  INSURANCE_EXTRA,
  INSURED_FOR_FIELD,
  isStarted,
  MODIFIED_PRICE_EXTRA,
  pickupStopInfo,
  RETURN_EXTRA,
  SHIPMENTS_FIELD,
  stopInfo,
  TRIGGER_FORM_CHANGE,
} from 'modules/shipper-order/Form/constants';

import { blockSomeFieldsForQuote } from 'modules/shipper-order/Form/utils';

import { ChangesHandler } from 'utils/formik';
import { connect } from 'react-redux';
import TenantStopSection from './IntraserSections/TenantStopSection';
import TenantLoadSection from './IntraserSections/TenantLoadSection';
import TenantReferenceSection from './IntraserSections/TenantReferenceSection';

const StyledLabel = styled.h3`
  font-size: 20px;
  font-weight: bold;
`;
const Separator = styled.div`
  border-bottom: 0.5px solid var(--grey200);
  border-top: 0.5px solid var(--grey200);
  background: var(--whiteCaniche);
  width: calc(100% + 4.8rem);
  margin: 15px 0 15px;
  margin-left: -2.4rem;
  padding: 0 2.4rem 0;
`;

class ShipperOrderForm extends Component {
  constructor(props) {
    super(props);
    this.getCommonStopProps = this.getCommonStopProps.bind(this);
    this.isDistribution = this.isDistribution.bind(this);
    this.isCollection = this.isCollection.bind(this);
    this.renderSimpleStop = this.renderSimpleStop.bind(this);
    this.handleExtraHandlingChangeInStop = this.handleExtraHandlingChangeInStop.bind(this);
    this.HandleExtraHandlingChangeInShipments =
      this.HandleExtraHandlingChangeInShipments.bind(this);
    this.handleExtraHandlingChange = this.handleExtraHandlingChange.bind(this);
    this.getNewShipment = this.getNewShipment.bind(this);
    this.getTimeRestrictions = this.getTimeRestrictions.bind(this);
    this.handleShipperChange = this.handleShipperChange.bind(this);
    this.handleUserChange = this.handleUserChange.bind(this);
  }

  componentDidMount() {
    const { validateForm, setFieldValue } = this.props;
    /*
      This is only for trigger a change in the form,
      and then trigger a quote.
    */
    setFieldValue(TRIGGER_FORM_CHANGE, true);
    validateForm();
  }

  getCommonStopProps() {
    const { shipperAddresses, userInfo, addAddress } = this.props;

    return {
      shipperAddresses,
      userInfo,
      onNewAddress: addAddress,
      timeRestrictions: this.getTimeRestrictions(),
    };
  }

  isDistribution() {
    const { values } = this.props;
    const { shipper_order_type } = values || {};
    return shipper_order_type === DISTRIBUTION;
  }

  isCollection() {
    const { values } = this.props;
    const { shipper_order_type } = values || {};
    return shipper_order_type === COLLECTION;
  }

  renderSimpleStop({ addressSuffix, stopPreffix }) {
    const { values, setFieldValue } = this.props;

    const { shipments = [] } = values;
    const isRepeated = !!values.repeated_from;

    if (
      (this.isDistribution() && stopPreffix !== 'pickup') ||
      (this.isCollection() && stopPreffix !== 'dropoff') ||
      shipments.length === 0
    ) {
      return null;
    }

    const name = this.isDistribution()
      ? [SHIPMENTS_FIELD, 0]
      : [SHIPMENTS_FIELD, shipments.length - 1];

    const handlingExtraName = stopPreffix === 'pickup' ? HANDLING_LOADING : HANDLING_UNLOADING;
    const shipmentExtras = shipments[0]?.extras || [];

    return (
      <TenantStopSection
        name={name}
        locator={true}
        addressSuffix={addressSuffix}
        stopPreffix={stopPreffix}
        isMainStop={true}
        showExtraInfo
        onHandlingExtraChange={this.handleExtraHandlingChange(
          [SHIPMENTS_FIELD, 0],
          shipmentExtras,
          true
        )}
        handlingExtraName={handlingExtraName}
        handlingExtraChecked={shipmentExtras.includes(handlingExtraName)}
        isRepeated={isRepeated}
        onTimeChange={(name, value, hour) => {
          let field = name.split('.').pop();
          if (!hour) {
            field = field.split('_').slice(0, 2).join('_');
          }

          setFieldValue(
            'shipments',
            shipments.map((shipment) =>
              !hour
                ? {
                    ...shipment,
                    [`${field}_to`]: value,
                    [`${field}_from`]: value,
                  }
                : {
                    ...shipment,
                    [field]: value,
                  }
            )
          );
        }}
        {...this.getCommonStopProps()}
      />
    );
  }

  handleExtraHandlingChangeInStop(shipmentField, modifiedExtraName, value) {
    const { setFieldValue, values } = this.props;
    const hasStops = values.stops !== undefined && values.stops.length >= 2;
    if (hasStops) {
      const shipmentIndex = shipmentField[1];
      const shipmentId = values.shipments[shipmentIndex].id.toString();
      const possibleStops = values.stops.filter((s) => {
        const stopShipmentIds = Object.keys(s.shipments);
        return stopShipmentIds.includes(shipmentId);
      });
      const stop = possibleStops.find((s) => {
        return (
          (modifiedExtraName === 'HANDLING_LOADING' && s.stop_type === 'PICKUP') ||
          (modifiedExtraName === 'HANDLING_UNLOADING' && s.stop_type === 'DROPOFF')
        );
      });
      const stopIndex = values.stops.findIndex((s) => s === stop);
      const stopFieldName = ['stops', stopIndex, 'extras'];
      const stopExtras = stop.extras || [];
      return value
        ? setFieldValue(stopFieldName, [...stopExtras, modifiedExtraName])
        : setFieldValue(
            stopFieldName,
            stopExtras.filter((extra) => extra !== modifiedExtraName)
          );
    }
  }

  HandleExtraHandlingChangeInShipments(
    shipmentField,
    currentShipmentExtras,
    applyToAllShipments,
    modifiedExtraName,
    value
  ) {
    const { setFieldValue, values } = this.props;
    const shipmentFieldName = [...shipmentField, EXTRAS_FIELD];

    // when changing the extra for main shipment (pickup for distribution or dropoff for collection)
    // we applay that extra to all required shipments
    if (applyToAllShipments) {
      const { shipments = [] } = values;
      shipments.map((s, index) => {
        const dynamicShipmentFieldName = [shipmentField[0], index, EXTRAS_FIELD];
        const dynamicShipmentCurrentExtras = s.extras || [];

        return value
          ? setFieldValue(dynamicShipmentFieldName, [
              ...dynamicShipmentCurrentExtras,
              modifiedExtraName,
            ])
          : setFieldValue(
              dynamicShipmentFieldName,
              dynamicShipmentCurrentExtras.filter((extra) => extra !== modifiedExtraName)
            );
      });
    }
    // when changing a regular shipment only apply extra to that shipment
    else {
      return value
        ? setFieldValue(shipmentFieldName, [...currentShipmentExtras, modifiedExtraName])
        : setFieldValue(
            shipmentFieldName,
            currentShipmentExtras.filter((extra) => extra !== modifiedExtraName)
          );
    }
  }

  handleExtraHandlingChange(
    shipmentField = [],
    currentShipmentExtras = [],
    applyToAllShipments = false
  ) {
    // modifiedExtraName will be the EXTRA that goes in the object
    //    such as HANDLING_LOADING or HANDLING_UNLOADING
    // value is the next value for this extra (should add or should remove)
    return (modifiedExtraName, value) => {
      this.handleExtraHandlingChangeInStop(shipmentField, modifiedExtraName, value);
      this.HandleExtraHandlingChangeInShipments(
        shipmentField,
        currentShipmentExtras,
        applyToAllShipments,
        modifiedExtraName,
        value
      );
    };
  }

  getNewShipment() {
    const { values } = this.props;
    const { shipments = [] } = values;

    const copiedAddress = this.isDistribution() ? 'address_from' : 'address_to';
    const copiedTimes = this.isDistribution() ? 'pickup_' : 'dropoff_';
    const copiedTimeFrom = `${copiedTimes}time_from`;
    const copiedTimeTo = `${copiedTimes}time_to`;

    const referenceShipment = this.isDistribution()
      ? shipments[0]
      : shipments[shipments.length - 1];
    const copiedExtras = referenceShipment.extras || [];
    const referenceLoad = (referenceShipment.loads || [])[0];
    const newShipment = baseShipment({
      loads: [
        baseLoad({
          item_height: referenceLoad.item_height,
        }),
      ],
      extras: copiedExtras,
      [copiedAddress]: referenceShipment[copiedAddress],
      [copiedTimeFrom]: referenceShipment[copiedTimeFrom],
      [copiedTimeTo]: referenceShipment[copiedTimeTo],
    });

    return newShipment;
  }

  getTimeRestrictions() {
    const { values } = this.props;
    const { shipments = [] } = values;

    const referenceShipment = this.isDistribution()
      ? shipments[0]
      : shipments[shipments.length - 1];
    const timeProp = this.isDistribution() ? 'pickup_' : 'dropoff_';
    const timePropFrom = `${timeProp}time_from`;

    const minDate = referenceShipment[timePropFrom]
      ? moment(referenceShipment[timePropFrom])
      : undefined;

    return {
      minDate,
    };
  }

  handleShipperChange(shipper) {
    const { shipperChange, setFieldValue, setValues, values } = this.props;

    shipperChange({
      shipper,
      setFieldValue,
      setValues,
      values,
    });
  }

  handleUserChange(user) {
    const { userChange } = this.props;
    userChange({ user });
  }

  render() {
    const {
      isEdition,
      shipperUsers,
      userInfo,
      values,
      handleSubmit,
      setFieldValue,
      onClose,
      submitText,
      showCloseWithoutSavingButton,
      activeExtras,
      formChange,
      quote,
      isValid,
      initialValues,
      showDriverAndVehicle,
      submiting,
      cancellationValues,
      disabledExtras,
      allowAddNewStop,
      extraComponent: ExtraComponent,
      shipperWarning,
      showOnlyQuote,
      intl,
    } = this.props;

    const { shipper_order_type, shipments = [], extras = [], shipper } = values;

    const t = intl.messages.modules.shipper_order.form;
    const { addressSuffix, stopPreffix } = stopInfo[shipper_order_type] || {};
    const isRepeated = !!values.repeated_from;
    const forceDisableEdition = false;
    const hasModifiedExtra = !!quote?.extra_prices?.find(
      (extra) => extra.concept === MODIFIED_PRICE_EXTRA
    );
    const hasReturnExtra = !!quote?.extra_prices?.find((extra) => extra.concept === RETURN_EXTRA);
    const priceHasToBeSetManually = quote.is_manual && !hasModifiedExtra && !hasReturnExtra;

    const getKey = (key) => {
      if (this.isCollection()) return key;
      return key + 1 === shipments.length ? 0 : key + 1;
    };

    return (
      <form onSubmit={handleSubmit}>
        {!showOnlyQuote && (
          <>
            <Flexer.Parent>
              <StyledLabel>{t.section_titles.client}</StyledLabel>
            </Flexer.Parent>
            <Flexer.Parent mBottom="M">
              <Flexer.Child mRight="S" data-e2e="shipperOrder_edit__shipper">
                <ShipperSelector
                  name="shipper"
                  onChange={this.handleShipperChange}
                  disabled={false}
                />
              </Flexer.Child>
              <Flexer.Child mRight="S" data-e2e="shipperOrder_edit__shipper_user">
                <ShipperUserSelector
                  name="user"
                  shipperUsers={shipperUsers}
                  onChange={this.handleUserChange}
                  disabled={false}
                />
              </Flexer.Child>
              <Flexer.Child mLeft="S" flexGrow="1">
                <ClientReference
                  name={['shipper', 'external_id']}
                  value={shipper?.external_id}
                  disabled
                />
              </Flexer.Child>
            </Flexer.Parent>

            <StyledLabel>{t.section_titles.references}</StyledLabel>
            <TenantReferenceSection values={values} intl={intl} />

            {this.renderSimpleStop(pickupStopInfo)}

            <FieldArray
              name="shipments"
              render={(shipmentsList) => (
                <Fragment>
                  {this.isDistribution() && allowAddNewStop && !isStarted(values) && (
                    <Spacer mBottom="M">
                      <Button
                        data-testid="newshipment-button"
                        text={t.add_button[stopPreffix]}
                        ghost
                        onClick={() => {
                          shipmentsList.push(this.getNewShipment());
                          setFieldValue(
                            EXTRAS_FIELD,
                            extras.filter((extra) => extra !== INSURANCE_EXTRA)
                          );
                          setFieldValue(INSURED_FOR_FIELD, null);
                        }}
                      />
                    </Spacer>
                  )}

                  {(shipments || []).map((shipment, key) => (
                    <TenantStopSection
                      key={getKey(key)}
                      name={[SHIPMENTS_FIELD, getKey(key)]}
                      shipperOrderType={shipper_order_type}
                      locator={getKey(key) === 0}
                      allowRemove={shipments.length > 2 && getKey(key) > 0 && !isStarted(values)}
                      allowMoveDown={
                        this.isCollection()
                          ? getKey(key) < shipments.length - 1 &&
                            getKey(key) > 0 &&
                            !isStarted(values)
                          : getKey(key) < shipments.length - 1 && !isStarted(values)
                      }
                      allowMoveUp={
                        this.isCollection()
                          ? getKey(key) > 1 && !isStarted(values)
                          : getKey(key) > shipments.length - 2 && !isStarted(values)
                      }
                      onMoveUp={() => shipmentsList.move(getKey(key), getKey(key) - 1)}
                      onMoveDown={() => shipmentsList.move(getKey(key), getKey(key) + 1)}
                      onRemove={() => shipmentsList.remove(getKey(key))}
                      addressSuffix={addressSuffix}
                      stopPreffix={stopPreffix}
                      isRepeated={isRepeated}
                      isEdition={isEdition}
                      shipment={shipments[getKey(key)]}
                      {...this.getCommonStopProps()}
                    />
                  ))}

                  {!this.isDistribution() && allowAddNewStop && !isStarted(values) && (
                    <Spacer mBottom="M">
                      <Button
                        data-testid="newshipment-button"
                        text={t.add_button[stopPreffix]}
                        ghost
                        onClick={() => {
                          shipmentsList.push(this.getNewShipment());
                          setFieldValue(
                            EXTRAS_FIELD,
                            extras.filter((extra) => extra !== INSURANCE_EXTRA)
                          );
                          setFieldValue(INSURED_FOR_FIELD, null);
                        }}
                      />
                    </Spacer>
                  )}
                </Fragment>
              )}
            />
            {this.renderSimpleStop(dropoffStopInfo)}

            <Separator />
            <StyledLabel>{t.section_titles.container}</StyledLabel>
            <TenantLoadSection name={[`shipments.0.loads[0]`]} shipments={shipments} intl={intl} />

            <Separator />
            {showDriverAndVehicle && (
              <Spacer mBottom="M">
                <StyledLabel>{t.section_titles.driver}</StyledLabel>
                <DriverVehicleSection />
              </Spacer>
            )}

            <Separator>
              <StyledLabel>{t.section_titles.price_modifiers}</StyledLabel>
              <ExtrasSection
                currency={userInfo.currency}
                activeExtras={activeExtras}
                isManual={quote.is_manual}
                cancellationValues={cancellationValues}
                disabledExtras={disabledExtras}
                extrasAmountsField="extra_prices"
                quote={quote}
              />
              {!!ExtraComponent && (
                <Spacer mBottom="M">
                  <ExtraComponent
                    setFieldValue={setFieldValue}
                    values={values}
                    currency={userInfo.currency}
                  />
                </Spacer>
              )}
            </Separator>
          </>
        )}

        {forceDisableEdition ? (
          <Spacer mBottom="M">
            <Feedback size="wideS" skin="warning">
              {t.manual_error}
            </Feedback>
          </Spacer>
        ) : (
          <Fragment>
            <Spacer mBottom="M">
              {!submiting && quote.loading && !quote.ready && isValid && <Loading />}
              {((!quote.loading && !quote.ready && !showOnlyQuote) || !isValid) && (
                <Feedback size="wideS" skin="warning">
                  {t.review_errors}
                  {Object.keys(this.props.errors).map(
                    (message) =>
                      !!t.pricing_engine_errors[message] && (
                        <div style={{ fontWeight: 'normal' }}>
                          {t.pricing_engine_errors[message]} - {this.props.errors[message]}
                        </div>
                      )
                  )}
                </Feedback>
              )}
              {quote.ready && isValid && !priceHasToBeSetManually ? (
                <Fragment>
                  <Title text={t.price} size="XL" level="h2" />
                  <Flexer.Parent>
                    {quote.recurring_order_quotes.length ? (
                      quote.recurring_order_quotes.map(
                        (q, index) =>
                          !!q && (
                            <StyledThreeColumnsChild key={index}>
                              <SummarySection
                                insideTitle={
                                  quote.recurring_order_quotes.length > 1
                                    ? t.recurring_order_day_types[q.day_type]
                                    : null
                                }
                                currency={userInfo.currency}
                                quote={q}
                              />
                            </StyledThreeColumnsChild>
                          )
                      )
                    ) : (
                      <StyledThreeColumnsChild>
                        <SummarySection
                          currency={userInfo.currency}
                          quote={quote}
                          previousNetValue={initialValues.net_full_price}
                        />
                      </StyledThreeColumnsChild>
                    )}
                  </Flexer.Parent>
                </Fragment>
              ) : null}
              {priceHasToBeSetManually && (
                <Spacer mTop="M">
                  <ManualPriceWarning />
                </Spacer>
              )}
            </Spacer>
            {quote.ready &&
              isValid &&
              initialValues.net_full_price &&
              !priceHasToBeSetManually &&
              Number(quote.net_full_price) !== Number(initialValues.net_full_price) && (
                <Spacer mBottom="M">
                  <Feedback size="wideS" skin="warning">
                    {t.alert_price_change}
                  </Feedback>
                </Spacer>
              )}

            {submiting && (
              <Spacer mBottom="S">
                <StyledLoading />
              </Spacer>
            )}
          </Fragment>
        )}

        {!!shipperWarning && (
          <Spacer mBottom="M">
            <Feedback size="wideS" skin="warning">
              {t.shipper_warning}
            </Feedback>
          </Spacer>
        )}

        {!showOnlyQuote && (
          <>
            <Flexer.Parent>
              <Flexer.Child>
                <Button
                  data-testid="submit-button"
                  type="submit"
                  text={submitText}
                  disabled={
                    forceDisableEdition ||
                    !isValid ||
                    !quote.ready ||
                    submiting ||
                    shipperWarning ||
                    priceHasToBeSetManually
                  }
                  dataE2E="shipperOrder_edit__update"
                />
              </Flexer.Child>
              {showCloseWithoutSavingButton && (
                <Flexer.Child>
                  <Button skin="secondary" nude text={t.close_no_save} onClick={onClose} />
                </Flexer.Child>
              )}
            </Flexer.Parent>
          </>
        )}
        <ChangesHandler onChange={formChange} blockChangesTrigger={blockSomeFieldsForQuote} />
      </form>
    );
  }
}

ShipperOrderForm.propTypes = {
  isEdition: PropTypes.bool,
  shipperChange: PropTypes.func,
  shipperUsers: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
    })
  ),
  shipperAddresses: PropTypes.array,
  shipperOrderId: PropTypes.number,
  userInfo: PropTypes.object.isRequired,
  addAddress: PropTypes.func.isRequired,
  values: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  userChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  submitText: PropTypes.string,
  showCloseWithoutSavingButton: PropTypes.bool,
  activeExtras: PropTypes.array,
  formChange: PropTypes.func,
  quote: PropTypes.object,
  isValid: PropTypes.bool.isRequired,
  setValues: PropTypes.func.isRequired,
  validateForm: PropTypes.func,
  showDriverAndVehicle: PropTypes.bool,
  initialValues: PropTypes.object,
  submiting: PropTypes.bool,
  cancellationValues: PropTypes.array.isRequired,
  disabledExtras: PropTypes.array,
  allowAddNewStop: PropTypes.bool,
};

ShipperOrderForm.defaultProps = {
  shipperChange: () => {
    /*noop*/
  },
  shipperUsers: [],
  shipperAddresses: [],
  shipperOrderId: null,
  values: {},
  errors: {},
  handleSubmit: () => {
    /*noop*/
  },
  submitText: '',
  showCloseWithoutSavingButton: true,
  activeExtras: [],
  formChange: () => {
    /*noop*/
  },
  quote: {},
  setValues: () => {
    /*noop*/
  },
  validateForm: () => {
    /*noop*/
  },
  showDriverAndVehicle: false,
  initialValues: {},
  submiting: false,
  cancellationValues: [],
  disabledExtras: [],
  allowAddNewStop: true,
};

const mapStateToProps = (state) => ({
  shipperUsers: getShipperUsers(state),
  shipperAddresses: getShipperAddresses(state),
  mainLoading: isMainLoading(state),
  submiting: isSubmiting(state),
  userInfo: getUserInfo(state),
  quote: getQuote(state),
});

const actionCreators = {
  shipperChange,
  userChange,
  addAddress,
  formChange,
};

export default connect(mapStateToProps, actionCreators)(injectIntl(ShipperOrderForm));
